import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import commaNumber from 'comma-number';
import { Sparkles } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import Transition from '@/components/Common/Transition';
import Sorter from '@/components/DataTable/Sorter';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import { usePublic } from '@/contexts/PublicContext';
import AskSynclyPanel from '@/features/AskSyncly/components/AskSynclyPanel';
import FeedbackCard from '@/features/Taxonomy/components/FeedbackCard/FeedbackCard';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useKeymap from '@/hooks/pure/useKeymap';
import { cn } from '@/lib/cn';
import { Button as ShadcnButton } from '@/shared/ui';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
  type DetailModalOptions,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { detailModalStyle } from '@/styles/style';

import { FEEDBACK_SORT_OPTIONS } from '../../configs/feedback-sort-options';
import { useFeedbackSortState } from '../../hooks/useFeedbackSortState';
import { FeedbackExport } from '../FeedbackExport/FeedbackExport';
import { isTimeSeriesPeakAnalysisFilterSet } from './peak-analysis';
import { PeakAnalysisPanel } from './peak-analysis-panel';
import type { FeedbackBlockListModalOptions } from './types';
import { usePeakAnalysis } from './use-peak-analysis';

type Props = {
  alias: string;
  pos: number;
  options: DetailModalOptions<FeedbackBlockListModalOptions>;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const FeedbackBlockListModal = (props: Props) => {
  const isPublic = usePublic();
  const scrollRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const chatMap = useHeySynclyStore((state) => state.chatMap);
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const setPopup = usePopup();
  const [chatAlias, setChatAlias] = useState<string | null>(null);
  const [sortStatus, setSortStatus] = useFeedbackSortState();
  const chat = chatAlias ? chatMap.get(chatAlias) : undefined;
  const peakAnalysisOptions = props.options.peakAnalysis;
  const showPeakAnalysisButton =
    isTimeSeriesPeakAnalysisFilterSet(peakAnalysisOptions);
  const { isPeakAnalysisLoading, peakAnalysisState, startPeakAnalysis } =
    usePeakAnalysis(peakAnalysisOptions);

  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedbackState>(null);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        props.alias,
        ...props.options.queryKeys,
        sortStatus.sortBy,
        sortStatus.orderStatus,
      ],
      queryFn: async ({ pageParam }) => {
        return await props.options.fetcher(sortStatus, pageParam);
      },
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage?.paginate_token,
    });

  const flattenData = useMemo(() => {
    return data?.pages
      .flat()
      .map((i) => i.list)
      .flat();
  }, [data?.pages]);

  const onFeedbackItemClick = (state: SelectedFeedbackState) => {
    if (!state) return;
    const openFeedbackModal = () => {
      if (isPublic) {
        setSelectedFeedback(state);
        setNewModal({
          alias: state.feedbackAlias,
          type: DETAIL_MODAL_TYPE.STATIC_FEEDBACK,
          idx: 1,
          keys: [state.blockAlias],
          options: {
            focusedBlock: state.blockAlias,
          },
        });
      } else {
        setSelectedFeedback(state);
        setNewModal({
          alias: state.feedbackAlias,
          type: DETAIL_MODAL_TYPE.FEEDBACK,
          idx: 1,
          keys: [state.blockAlias],
          options: {
            focusedBlock: state.blockAlias,
          },
        });
      }
    };
    if (selectedFeedback?.feedbackAlias === state.feedbackAlias) {
      if (selectedFeedback?.blockAlias === state.blockAlias) {
        setSelectedFeedback(null);
      } else {
        openFeedbackModal();
      }
    } else {
      openFeedbackModal();
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: 0,
    });
  }, [props.alias]);

  useTrackModal({
    domain: props.options.domain,
    alias: props.alias,
  });

  useEffect(() => {
    if (
      !showPeakAnalysisButton ||
      !peakAnalysisOptions?.autoStart ||
      peakAnalysisState.status !== 'idle'
    ) {
      return;
    }

    void startPeakAnalysis();
  }, [
    peakAnalysisOptions?.autoStart,
    peakAnalysisState.status,
    showPeakAnalysisButton,
    startPeakAnalysis,
  ]);

  const onCloseClick = () => {
    if (isPeakAnalysisLoading) {
      setPopup({
        type: 'confirm',
        popupTheme: 'negative',
        title: 'Stop analysis and close?',
        message:
          'Closing this window will hide the in-progress analysis result.',
        confirmText: 'Close',
        cancelText: 'Keep analyzing',
        onConfirm: () => {
          props.closeModal();
        },
      });
      return;
    }

    if (chatAlias && chat?.alias === chatAlias) {
      setPopup({
        type: 'confirm',
        popupTheme: 'negative',
        title: 'Would you like to close this window?',
        message: 'If you close the window, the content will be deleted.',
        confirmText: 'Okay',
        onConfirm: () => {
          props.closeModal();
        },
      });
    } else {
      props.closeModal();
    }
  };

  useKeymap('Escape', onCloseClick);

  return (
    <ModalStyle status={true} pos={props.pos} ref={modalRef}>
      <Wrapper f={1}>
        <Flex j="flex-end" a="center" style={{ padding: '16px 32px' }}>
          <Flex g="6px">
            {props.options.exportFetcher && (
              <FeedbackExport onExportClick={props.options.exportFetcher}>
                <Button variant="outlined" leftIcon={<DownloadOutlined />}>
                  Export to CSV
                </Button>
              </FeedbackExport>
            )}

            <IconButton
              icon={<CloseOutlined />}
              onClick={onCloseClick}
              tooltip="Close"
              shortcut="ESC"
            />
          </Flex>
        </Flex>
        <Divider />
        <Space h={24} />
        <VFlex padding="0px 32px" g={12}>
          <Flex g={4} j="space-between" a="center">
            <Typo type="label1" className="text-gray-600">
              {props.options.title}
            </Typo>

            {showPeakAnalysisButton && (
              <ShadcnButton
                type="button"
                variant="outline"
                size="xs"
                disabled={
                  isPeakAnalysisLoading ||
                  peakAnalysisState.status === 'success'
                }
                onClick={() => void startPeakAnalysis()}
                className="border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary"
              >
                <Sparkles className="size-4" />
                Analyze Change Drivers
              </ShadcnButton>
            )}
          </Flex>
          {props.options.subTitle}
        </VFlex>
        <Space h={24} />
        <div className="px-8 flex justify-between items-center">
          <Typo type="label2">
            Total feedback : {commaNumber(data?.pages[0]?.count || 0)}
          </Typo>
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
        </div>
        <ScrollBody ref={scrollRef}>
          <Space h={24} />
          {peakAnalysisState.status !== 'idle' && (
            <>
              <div className="px-8">
                <Transition status>
                  <PeakAnalysisPanel
                    state={peakAnalysisState}
                    onRetry={() => void startPeakAnalysis()}
                  />
                </Transition>
              </div>
              <Space h={12} />
            </>
          )}
          <List
            isLoading={isLoading}
            data={flattenData}
            className={cn(
              'gap-2 px-8 h-auto overflow-visible',
              props.options.socketInfo ? 'pb-50' : 'pb-6',
            )}
            render={(f) => (
              <FeedbackCard
                selectedFeedback={selectedFeedback}
                selectedUnit={null}
                setSelectedFeedback={onFeedbackItemClick}
                key={f.alias}
                feedback={f}
              />
            )}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </ScrollBody>
        {props.options.socketInfo && (
          <AskSynclyPanel
            key={JSON.stringify(props.options.socketInfo)}
            socketInfo={props.options.socketInfo}
            onSocketConnectChange={setChatAlias}
            chat={chat}
          />
        )}
      </Wrapper>
    </ModalStyle>
  );
};

const Wrapper = styled(VFlex)`
  height: 100%;
  min-height: 0;
`;

const ScrollBody = styled(VFlex)`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle};
`;

export default FeedbackBlockListModal;
